// v2 deer — a full red-deer build: slim legs with dark hooves, a deep chest
// tapering to slimmer hindquarters, a long raised neck carrying a refined head
// with a tapered muzzle and flared ears, two-tine branched antlers, and the
// cream rump patch + scut every deer flashes as it bolts. Two coats: summer
// chestnut and grey-brown winter. Ported 1:1 from age-of-mcp's v2/deer.ts,
// using the library's shared `critter` (quadLegs) helper.
import * as THREE from "three";
import { defineModel, critter, type Part } from "../../lib";

const COATS = [
  { body: "#92653a", chest: "#9d7044", head: "#a87c4c", muz: "#8a6038", leg: "#6e4d2c" },
  { body: "#7d6b52", chest: "#877458", head: "#93805f", muz: "#786750", leg: "#5b4c3a" },
];

function deerParts(v: number): Part[] {
  const c = COATS[v % COATS.length]!;
  const parts: Part[] = critter.quadLegs({
    dx: 0.075, dz: 0.038, bodyY: 0.2, rTop: 0.0085, rFoot: 0.0062,
    color: c.leg, hoof: { color: "#2e241a", r: 0.0085, h: 0.014 },
  });
  parts.push(
    // torso — deep through the chest, slimmer behind
    { geometry: new THREE.IcosahedronGeometry(0.062, 0), color: c.body,
      position: [0, 0.2, 0], scale: [1.55, 0.95, 0.85] },
    { geometry: new THREE.IcosahedronGeometry(0.05, 0), color: c.chest,
      position: [0.058, 0.196, 0], scale: [1.0, 1.05, 0.92] },
    { geometry: new THREE.IcosahedronGeometry(0.047, 0), color: c.body,
      position: [-0.068, 0.205, 0], scale: [1.0, 0.95, 0.9] },
    // cream rump patch + scut
    { geometry: new THREE.IcosahedronGeometry(0.027, 0), color: "#e9ddc5",
      position: [-0.103, 0.21, 0], scale: [0.55, 0.9, 1.0] },
    { geometry: new THREE.IcosahedronGeometry(0.013, 0), color: "#f2ead8",
      position: [-0.108, 0.232, 0] },
    // long raised neck
    { geometry: new THREE.CylinderGeometry(0.016, 0.026, 0.13, 5), color: c.chest,
      position: [0.112, 0.268, 0], rotation: [0, 0, -0.55] },
    // refined head with a tapered muzzle and dark nose
    { geometry: new THREE.IcosahedronGeometry(0.028, 0), color: c.head,
      position: [0.152, 0.334, 0], scale: [1.25, 1.0, 0.9] },
    { geometry: new THREE.ConeGeometry(0.012, 0.034, 4), color: c.muz,
      position: [0.186, 0.327, 0], rotation: [0, 0, -Math.PI / 2] },
    { geometry: new THREE.IcosahedronGeometry(0.0055, 0), color: "#241b12",
      position: [0.202, 0.327, 0] },
    // big flared ears
    { geometry: new THREE.ConeGeometry(0.009, 0.026, 4), color: c.head,
      position: [0.142, 0.358, 0.02], rotation: [0.55, 0, -0.15] },
    { geometry: new THREE.ConeGeometry(0.009, 0.026, 4), color: c.head,
      position: [0.142, 0.358, -0.02], rotation: [-0.55, 0, -0.15] },
    // two-tine antlers — a main beam swept back, a brow tine forward
    { geometry: new THREE.CylinderGeometry(0.0042, 0.0042, 0.06, 3), color: "#cdbb96",
      position: [0.148, 0.385, 0.012], rotation: [0.22, 0, 0.25] },
    { geometry: new THREE.CylinderGeometry(0.0042, 0.0042, 0.06, 3), color: "#cdbb96",
      position: [0.148, 0.385, -0.012], rotation: [-0.22, 0, 0.25] },
    { geometry: new THREE.CylinderGeometry(0.003, 0.003, 0.034, 3), color: "#cdbb96",
      position: [0.132, 0.4, 0.019], rotation: [0.42, 0, -0.45] },
    { geometry: new THREE.CylinderGeometry(0.003, 0.003, 0.034, 3), color: "#cdbb96",
      position: [0.132, 0.4, -0.019], rotation: [-0.42, 0, -0.45] },
  );
  return parts;
}

export const deerV2 = defineModel({
  id: "deer-v2",
  batchId: "fable",
  name: "Deer (v2)",
  tags: ["creature","deer","low-poly","age-of-mcp","fable"],
  summary: "Low-poly deer.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.15,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => deerParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => deerParts(1) },
  ],
  animations: [],
});
